import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Lightbox, {
  LightboxProps,
} from '@/components/utilities/lightbox/lightbox';
import { useI18n } from '@/libraries/contexts/i18n-context';
import MediaLightboxItem from './media-lightbox-item/media-lightbox-item';
import styles from './media-lightbox.module.scss';

export interface MediaSlide {
  media: Content.Properties.ImageAndVideoGridItem;
  sideContent?: React.ReactNode;
}

export interface MediaLightboxProps {
  /** Whether to show lightbox fullscreen */
  fullscreen?: boolean;
  /** Whether the lightbox is open or not. */
  isOpen?: boolean;
  /** The media to display in the lightbox. */
  items: MediaSlide[];
  /** The callback to handle the close event. */
  onClose: () => void;
  /** The size of the lightbox. */
  size?: LightboxProps['size'];
  /** What item to show first */
  startIndex?: number;
}

const MediaLightbox = ({
  fullscreen = false,
  isOpen = false,
  items = [],
  onClose,
  size = 'medium',
  startIndex,
}: MediaLightboxProps) => {
  const { t } = useI18n();
  const swiperRef = useRef<SwiperInstance>();
  const [activeItemIndex, setActiveItemIndex] = useState(startIndex ?? 0);

  useEffect(() => {
    if (startIndex === undefined) return;

    swiperRef.current?.slideTo(startIndex, 0);
  }, [startIndex]);

  const handleNext = useCallback(() => {
    if (!swiperRef.current) return;

    swiperRef.current.slideNext();
  }, [swiperRef]);

  const handlePrevious = useCallback(() => {
    if (!swiperRef.current) return;

    swiperRef.current.slidePrev();
  }, [swiperRef]);

  useEffect(() => {
    if (!window) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious();
      }

      if (event.key === 'ArrowRight') {
        handleNext();
      }

      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Lightbox
      fullscreen={fullscreen}
      isOpen={isOpen}
      onClose={onClose}
      size={size}
    >
      <div
        className={classNames(styles.component, {
          [styles.isFullscreen]: fullscreen,
        })}
      >
        <div className={styles.counter}>
          <Typography tagStyle="labelMedium">{`${activeItemIndex + 1} / ${items.length}`}</Typography>
        </div>

        <Swiper
          className={styles.swiper}
          initialSlide={startIndex}
          loop={items.length > 1}
          onActiveIndexChange={({ realIndex }) => {
            setActiveItemIndex(realIndex);
          }}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          slidesPerView={1}
          wrapperClass={styles.wrapper}
        >
          {items.length > 1 && (
            <Button
              aria-label={t('items.common.words.previous')}
              className={styles.previous}
              icon="ChevronLeft"
              iconSize={16}
              onClick={handlePrevious}
              variant="tertiary"
            />
          )}

          {items.map((item, slideIndex) => (
            <SwiperSlide key={slideIndex} className={styles.slide}>
              <MediaLightboxItem
                item={item.media}
                preload={
                  isOpen &&
                  slideIndex >= activeItemIndex - 1 &&
                  slideIndex <= activeItemIndex + 1
                }
              />
            </SwiperSlide>
          ))}

          {items.length > 1 && (
            <Button
              aria-label={t('items.common.words.next')}
              className={styles.next}
              icon="ChevronRight"
              iconSize={16}
              onClick={handleNext}
              variant="tertiary"
            />
          )}
        </Swiper>

        {items[activeItemIndex]?.media?.captionText && (
          <Typography className={styles.caption} tagStyle="labelMedium">
            {items[activeItemIndex].media.captionText}
          </Typography>
        )}

        {items[activeItemIndex]?.sideContent && (
          <div className={styles.sideContent}>
            {items[activeItemIndex].sideContent}
          </div>
        )}
      </div>
    </Lightbox>
  );
};

MediaLightbox.displayName = 'MediaLightbox';

export default MediaLightbox;
